import React, { useCallback, useEffect, useState } from 'react';
import { Button, message } from 'antd';
import cx from 'classnames';
import { getDefaultProviderSignature, getProviderPrivateFileSigned } from 'api/common';
import { bindCredentialingSignature, getCredVerification } from 'api/credentialing';
import type { CredentialingSignatureVo } from 'api/credentialing/types';
import SignatureModal from 'components/ProviderSignature/SignatureModal';
import type { IProviderSignature } from 'types/treatment';
import s from './s.module.less';

export type TSignatureRecord = IProviderSignature & { id?: number; previewUrl?: string };

export type TSignee = { providerId: number; name: string; self: boolean };

interface Props {
    // solo：一个人（本人）；group：本次提交的每个 provider
    signees: TSignee[];
    group: boolean;
    // 每个 provider 的签名（id 用于提交）有变化时上报
    onChange: (signatures: Record<number, TSignatureRecord | null>) => void;
    // 补签弹窗（10/26）：不要区块自带的标题和说明（弹窗自己写），打开落在第一个没签的人，签完跳下一个
    bare?: boolean;
    advanceOnSign?: boolean;
}

export const SELF_CONSENT =
    'By selecting Confirm, I agree that this mark will be the electronic representation of my signature or initials whenever I use it, and that Klarity may place it on the enrollment forms it files for me.';
export const otherConsent = (name: string) =>
    `This mark is ${name}'s signature. By selecting Confirm, I confirm I have their permission to use it on the enrollment forms Klarity files for them.`;

const EditIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
);

// 取某个 provider 的默认签名（带预览地址）；providerId 传了就切 header 到该 provider
export const loadProviderSignature = async (providerId?: number): Promise<TSignatureRecord | null> => {
    try {
        const res: any = await getDefaultProviderSignature(providerId);
        const sig: TSignatureRecord | undefined = res?.error ? undefined : res?.data?.data;
        if (!sig?.id || !sig.signatureFileKey) return null;
        const pv: any = await getProviderPrivateFileSigned(sig.signatureFileKey);
        return { ...sig, previewUrl: pv?.data?.data?.singedUrl };
    } catch (e) {
        return null;
    }
};

// 有签名：Signature preview + Edit；没有：Add signature（协议页与 Settings 共用）
const SignatureSlot = ({
    rec,
    loaded,
    onEdit,
}: {
    rec: TSignatureRecord | null | undefined;
    loaded: boolean;
    onEdit: () => void;
}) =>
    rec ? (
        <>
            <div className={s.sigPreviewHeader}>
                <span>Signature preview</span>
                <Button type="text" size="small" className={s.sigEditBtn} onClick={onEdit}>
                    <EditIcon />
                    Edit
                </Button>
            </div>
            <div className={s.sigPreview}>
                {rec.previewUrl ? <img src={rec.previewUrl} alt="Signature" /> : rec.fullName}
            </div>
        </>
    ) : (
        <Button className={s.sigAddBtn} disabled={!loaded} onClick={onEdit}>
            Add signature
        </Button>
    );

// 原型 agreement 的签名区块（10/20）：签名只认 provider 自己 Klarity 账户上的那份（EHR 的 Add signature
// 设一次、每张表复用），这里只显示有没有并打开那个弹窗，不再内联手写。group 每个 provider 都要本人签名，
// owner 可以在这里替成员打开弹窗（签名存在成员名下，弹窗里确认已获本人许可）；全部签完才能 Confirm
export default function SignatureBlock({ signees, group, onChange, bare, advanceOnSign }: Props) {
    const [sigs, setSigs] = useState<Record<number, TSignatureRecord | null>>({});
    const [loaded, setLoaded] = useState(false);
    const [active, setActive] = useState(0);
    const [editing, setEditing] = useState<TSignee | null>(null);

    // group 一律切到该 provider 名下取（owner 本人也一样）；solo 取登录者本人
    const load = useCallback((p: TSignee) => loadProviderSignature(group ? p.providerId : undefined), [group]);

    const signeeKey = signees.map((p) => p.providerId).join(',');
    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        Promise.all(signees.map(load)).then((list) => {
            if (cancelled) return;
            const next: Record<number, TSignatureRecord | null> = {};
            signees.forEach((p, i) => {
                next[p.providerId] = list[i];
            });
            setSigs(next);
            setLoaded(true);
            if (advanceOnSign) {
                const firstMissing = signees.findIndex((p) => !next[p.providerId]);
                if (firstMissing >= 0) setActive(firstMissing);
            }
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signeeKey, load]);

    useEffect(() => {
        if (loaded) onChange(sigs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sigs, loaded]);

    // 弹窗保存后重新取一次默认签名：createProviderSignature 的回调里没有 id，提交要用 id
    const afterSaved = async (p: TSignee) => {
        setEditing(null);
        const rec = await load(p);
        const next = { ...sigs, [p.providerId]: rec };
        setSigs(next);
        if (advanceOnSign && rec) {
            const nextMissing = signees.findIndex((q) => !next[q.providerId]);
            if (nextMissing >= 0) setActive(nextMissing);
        }
    };

    const slot = (p: TSignee) => (
        <SignatureSlot rec={sigs[p.providerId]} loaded={loaded} onEdit={() => setEditing(p)} />
    );

    const doneCount = signees.filter((p) => !!sigs[p.providerId]).length;
    const current = signees[active] || signees[0];

    return (
        <div className={s.sigBlock}>
            {group ? (
                <>
                    {!bare && (
                        <>
                            <div className={s.esignLabel}>Signatures</div>
                            <p className={s.sigDesc}>
                                Payers require each provider&apos;s own signature on their enrollment forms, and nobody
                                can sign for someone else. A signature set here is saved to that provider&apos;s Klarity
                                account and reused on every form we file.
                            </p>
                        </>
                    )}
                    <div className={s.sigSummary}>
                        {doneCount} of {signees.length} providers signed
                    </div>
                    <div className={s.sigChips}>
                        {signees.map((p, i) => (
                            <button
                                type="button"
                                key={p.providerId}
                                className={cx(s.sigChip, i === active && s.sigChipActive)}
                                onClick={() => setActive(i)}
                            >
                                {loaded && !sigs[p.providerId] && <span className={s.sigChipDot} />}
                                {p.name}
                            </button>
                        ))}
                    </div>
                    {current && (
                        <div className={s.sigRow}>
                            <div className={s.sigRowName}>
                                {current.name}
                                {current.self && <span className={s.sigRowYou}> (you)</span>}
                            </div>
                            {slot(current)}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className={s.esignLabel}>Your signature</div>
                    <p className={s.sigDesc}>
                        Payers require your own signature on enrollment forms, and we cannot sign for you. Set it once
                        and we place it on every form you authorize below.
                    </p>
                    {signees[0] && slot(signees[0])}
                </>
            )}

            {editing && (
                <SignatureModal
                    open
                    onCancel={() => setEditing(null)}
                    onConfirm={() => afterSaved(editing)}
                    initialData={sigs[editing.providerId] || undefined}
                    providerId={group ? editing.providerId : undefined}
                    defaultFullName={editing.self ? undefined : editing.name}
                    consentText={editing.self ? SELF_CONSENT : otherConsent(editing.name)}
                />
            )}
        </div>
    );
}

// 取绑定在 credentialing 上的签名（#20：verification/detail 的 signature，不是 EHR 默认签名）
export const loadCredentialingSignature = async (providerId?: number): Promise<TSignatureRecord | null> => {
    try {
        const res: any = await getCredVerification(providerId);
        const sig: CredentialingSignatureVo | null | undefined = res?.error ? null : res?.data?.data?.signature;
        if (!sig?.signatureId) return null;
        return {
            id: sig.signatureId,
            fullName: sig.fullName || '',
            initials: sig.initials || '',
            signatureType: (sig.signatureType || undefined) as TSignatureRecord['signatureType'],
            fontName: sig.fontName || undefined,
            isDefault: 0,
            previewUrl: sig.signatureUrl || undefined,
        };
    } catch (e) {
        return null;
    }
};

// 签名弹窗确认后：重新取默认签名拿 id（create 回调里没有），再绑定到 credentialing；成功返回 true
export const bindLatestSignature = async (bindProviderId: number, overrideProviderId?: number): Promise<boolean> => {
    const rec = await loadProviderSignature(overrideProviderId);
    if (!rec?.id) {
        message.error('Failed to save the signature. Please try again.');
        return false;
    }
    const res: any = await bindCredentialingSignature([{ providerId: bindProviderId, signatureId: rec.id }]);
    if (res?.error) {
        message.error(res.error);
        return false;
    }
    return true;
};

// Settings · Verification（原型 10/26）：credentialing 签名和其他字段一样只读回显，没有时红字 Not set；
// providerId 传了就切到该 provider（group 选中成员时），不传取登录者本人
export const ProviderSignatureView = ({ providerId }: { providerId?: number }) => {
    const [rec, setRec] = useState<TSignatureRecord | null | undefined>(undefined);
    useEffect(() => {
        let cancelled = false;
        loadCredentialingSignature(providerId).then((r) => !cancelled && setRec(r));
        return () => {
            cancelled = true;
        };
    }, [providerId]);
    if (rec === undefined) return null;
    return rec?.previewUrl ? (
        <img className={s.sigViewImg} src={rec.previewUrl} alt="Signature" />
    ) : (
        <span className={rec ? undefined : s.sigViewUnset}>{rec ? rec.fullName : 'Not set'}</span>
    );
};

// Settings · Verification 编辑态的 Signature 区块：与协议页同一个预览和弹窗。弹窗确认后立即绑定
// （#20 /onboarding/signature），区块的 Cancel 只是关闭编辑（10/27 定）。
// bindProviderId 是被绑定签名的 provider；overrideProviderId 只在 group 切到成员时传
export const ProviderSignatureField = ({
    bindProviderId,
    overrideProviderId,
    name,
    self,
}: {
    bindProviderId: number;
    overrideProviderId?: number;
    name: string;
    self: boolean;
}) => {
    const [rec, setRec] = useState<TSignatureRecord | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [editing, setEditing] = useState(false);
    // 弹窗回填要 signatureFileKey，credentialing 签名只给 signatureUrl：打开时改用该 provider 的 EHR 默认签名
    // （绑定的就是它；没有时退回只带姓名缩写的 credentialing 签名）
    const [modalInitial, setModalInitial] = useState<TSignatureRecord | null>(null);
    const openEdit = async () => {
        setModalInitial((await loadProviderSignature(overrideProviderId)) || rec);
        setEditing(true);
    };
    const reload = useCallback(async () => {
        setRec(await loadCredentialingSignature(overrideProviderId));
        setLoaded(true);
    }, [overrideProviderId]);
    useEffect(() => {
        setLoaded(false);
        reload();
    }, [reload]);
    return (
        <>
            <SignatureSlot rec={rec} loaded={loaded} onEdit={openEdit} />
            {editing && (
                <SignatureModal
                    open
                    onCancel={() => setEditing(false)}
                    onConfirm={async () => {
                        setEditing(false);
                        if (await bindLatestSignature(bindProviderId, overrideProviderId)) reload();
                    }}
                    initialData={modalInitial || undefined}
                    providerId={overrideProviderId}
                    defaultFullName={self ? undefined : name}
                    consentText={self ? SELF_CONSENT : otherConsent(name)}
                />
            )}
        </>
    );
};
